import { notifyPdfLoadedSection } from '../PDFRenderer/PDFRendererActions';
import { PdfSectionType } from '../PDFRenderer/PDFRendererTypes';
import fetchWithDefaults from '../CommonUtils/fetchWithDefaults';
import { urlWithParams } from '../CommonUtils/urlWithParams';
import { HttpError } from '../errors/HttpError';
import { httpBodyOptions } from '../CommonUtils/httpBodyOptions';
import { abortRequest } from '../CommonUtils/abortRegistry';

const prefixer = name => `ADV.MONTE_CARLO_CHART.${name}`;

export const REQUEST_DATA = prefixer('REQUEST_DATA');
export const RECEIVE_DATA = prefixer('RECEIVE_DATA');
export const INVALIDATE_CACHE = prefixer('INVALIDATE_CACHE');
export const STAGE_WHAT_IFS = prefixer('STAGE_WHAT_IFS');
export const SET_UPDATE_CACHE_DATA = prefixer('SET_UPDATE_CACHE_DATA');

export const FAILED_TO_RECEIVE_DATA = prefixer('FAILED_TO_RECEIVE_DATA');

export const REQUEST_ABORTED = prefixer('REQUEST_ABORTED');

export const requestData = (clientId, requestedWith) => ({
  type: REQUEST_DATA,
  clientId,
  requestedWith,
});

export const receiveData = (clientId, data, requestedWith) => ({
  type: RECEIVE_DATA,
  clientId,
  data,
  requestedWith,
});
export const stageWhatIfs = (clientId, whatIfs) => ({
  type: STAGE_WHAT_IFS,
  clientId,
  whatIfs,
});

export const invalidateCache = clientId => ({
  type: INVALIDATE_CACHE,
  clientId,
});

export const setUpdateCacheData = (clientId, updateCacheData) => ({
  type: SET_UPDATE_CACHE_DATA,
  clientId,
  updateCacheData,
});

export const failedToReceiveData = (clientId, requestedWith) => ({
  type: FAILED_TO_RECEIVE_DATA,
  clientId,
  requestedWith,
});

export const requestAborted = clientId => ({
  type: REQUEST_ABORTED,
  clientId,
});

// Cancels any in-flight Monte Carlo request (e.g. when the user leaves the tab).
// The aborted fetch hits its AbortError branch and dispatches requestAborted,
// which clears the loading increment.
export const abortMcRequests = () => () => {
  abortRequest('GET /Consumer/MC');
  abortRequest('POST /Consumer/MC');
};

export const fetchMcData = (fpId, clientId) => async dispatch => {
  const requestedWith = {
    clientId,
    fpId,
    stagedWhatIfs: [],
  };

  dispatch(requestData(clientId, requestedWith));

  try {
    const response = await fetchWithDefaults(urlWithParams('/Consumer/MC', requestedWith), {
      abortKey: 'GET /Consumer/MC',
    });

    if (response.ok) {
      const { payload, success } = await response.json();

      if (success) {
        dispatch(receiveData(clientId, payload.viewData, requestedWith));
        dispatch(notifyPdfLoadedSection(PdfSectionType.RetirementMonteCarlo));
        return;
      }

      throw new Error('Response status is not success.');
    }

    throw new HttpError(response);
  } catch (err) {
    // A superseded request was aborted by a newer call; don't surface an error,
    // but still clear this request's loading increment.
    if (err instanceof DOMException && err.name === 'AbortError') {
      dispatch(requestAborted(clientId));
      return;
    }

    dispatch(failedToReceiveData(clientId, requestedWith));
    console.error(err); // eslint-disable-line no-console
  }
};

export const fetchPostMcData = (
  fpId,
  clientId,
  isPdf,
  stagedWhatIfs,
  FRAOption,
) => async dispatch => {
  const urlParams = {
    clientId,
    IsPdf: isPdf,
    fpId,
  };
  // stagedWhatIfs travels in the request body, but it's part of what identifies
  // this request, so track it here to tag the response (see receiveData usage).
  const requestedWith = { ...urlParams, stagedWhatIfs };

  dispatch(requestData(clientId, requestedWith));

  const body = Array.isArray(stagedWhatIfs)
    ? [{ ...stagedWhatIfs[0], ...FRAOption }]
    : [{ ...stagedWhatIfs, ...FRAOption }];

  try {
    const response = await fetchWithDefaults(urlWithParams('/Consumer/MC', urlParams), {
      method: 'POST',
      ...httpBodyOptions(body, true),
      abortKey: 'POST /Consumer/MC',
    });

    if (response.ok) {
      const { payload, success } = await response.json();

      if (success) {
        dispatch(receiveData(clientId, payload.viewData, requestedWith));
        return;
      }

      throw new Error('Response status is not success.');
    }

    throw new HttpError(response);
  } catch (err) {
    // A superseded request was aborted by a newer call; don't surface an error,
    // but still clear this request's loading increment.
    if (err instanceof DOMException && err.name === 'AbortError') {
      dispatch(requestAborted(clientId));
      return;
    }

    dispatch(failedToReceiveData(clientId, requestedWith));
    console.error(err.toString()); // eslint-disable-line no-console
  }
};
